import Phaser from "phaser";
import { scenarioById } from "../config/campaign";
import { TOWERS } from "../config/towers";
import { MAPS, CASTLE_APPROACH } from "../config/map";
import { loadSave, writeSave } from "../systems/save";
import { describeTowerRequirement, isTowerUnlocked } from "../systems/progression";
import {
  addButton,
  addCastleBackdrop,
  addOrnatePanel,
  addStatusBadge,
  COLORS,
  title,
} from "./shared";

const PAGE_SIZE = 8;

export class LoadoutScene extends Phaser.Scene {
  private page = 0;
  private selected = new Set<string>();
  private pageObjects: Phaser.GameObjects.GameObject[] = [];
  private save = loadSave();

  constructor() {
    super("Loadout");
  }

  create(): void {
    this.save = loadSave();
    addCastleBackdrop(this);
    this.cameras.main.setBackgroundColor(COLORS.ink);
    const map = MAPS[this.registry.get("selected-map-id") as string] ?? CASTLE_APPROACH;
    const scenario = scenarioById(this.registry.get("selected-scenario-id") as string | undefined);
    title(
      this,
      "Assemble the Royal Companies",
      `${map.displayName} • ${scenario?.displayName ?? "Easy"}: choose up to five unlocked tower companies.`,
    );
    this.selected = new Set(
      this.save.loadout.towerIds.filter((id) => {
        const tower = TOWERS[id];
        return tower ? isTowerUnlocked(tower, this.save.progress) : false;
      }),
    );
    addButton(this, 95, 42, 150, "‹ Back", () => this.scene.start("LevelSelect"));
    this.renderPage();
  }

  private renderPage(): void {
    this.pageObjects.forEach((object) => object.destroy());
    this.pageObjects = [];
    const towers = Object.values(TOWERS);
    const pageCount = Math.ceil(towers.length / PAGE_SIZE);
    this.page = Phaser.Math.Clamp(this.page, 0, Math.max(0, pageCount - 1));
    const entries = towers.slice(this.page * PAGE_SIZE, this.page * PAGE_SIZE + PAGE_SIZE);
    const columns = this.scale.width >= 920 ? 4 : this.scale.width >= 680 ? 3 : 2;
    const gap = 14;
    const cardWidth = Math.min(190, (this.scale.width - 52 - gap * (columns - 1)) / columns);
    const cardHeight = this.scale.height < 760 ? 156 : 172;
    const rows = Math.ceil(entries.length / columns);
    const gridWidth = columns * cardWidth + (columns - 1) * gap;
    const gridHeight = rows * cardHeight + (rows - 1) * gap;
    const startX = (this.scale.width - gridWidth) / 2 + cardWidth / 2;
    const top = Math.max(222, Math.min(250, (this.scale.height - gridHeight) / 2 + 36));
    const startY = top + cardHeight / 2;

    entries.forEach((tower, index) => {
      const row = Math.floor(index / columns);
      const column = index % columns;
      const x = startX + column * (cardWidth + gap);
      const y = startY + row * (cardHeight + gap);
      const unlocked = isTowerUnlocked(tower, this.save.progress);
      const selected = this.selected.has(tower.id);
      const panel = addOrnatePanel(this, x, y, {
        width: cardWidth,
        height: cardHeight,
        interactive: unlocked,
        selected,
        disabled: !unlocked,
      });
      const hitArea = panel.getData("hit-area") as Phaser.GameObjects.Rectangle | undefined;
      if (hitArea && unlocked) hitArea.on("pointerdown", () => this.toggleTower(tower.id));
      panel.setAlpha(0).setScale(0.92);
      this.tweens.add({
        targets: panel,
        alpha: unlocked ? 1 : 0.58,
        scaleX: 1,
        scaleY: 1,
        duration: 240,
        delay: index * 38,
        ease: "Back.Out",
      });
      this.pageObjects.push(panel);

      const iconPlate = this.add
        .circle(x, y - (cardHeight < 165 ? 36 : 42), 35, selected ? 0x7a5a23 : 0x1d1916, 0.92)
        .setStrokeStyle(2, selected ? COLORS.gold : 0x7a6854, 0.9);
      const icon = this.add
        .image(x, y - (cardHeight < 165 ? 36 : 42), tower.texture)
        .setScale(0.58)
        .setAlpha(unlocked ? 1 : 0.28);
      const name = this.add
        .text(x, y + 2, tower.displayName, {
          fontFamily: "Georgia, serif",
          fontSize: "13px",
          fontStyle: "bold",
          color: unlocked ? "#fff3cf" : "#85817b",
          align: "center",
          wordWrap: { width: cardWidth - 18 },
        })
        .setOrigin(0.5);
      const badge = addStatusBadge(
        this,
        x,
        y + 31,
        unlocked ? (selected ? "SELECTED" : `◆${tower.cost}`) : "LOCKED",
        selected ? "gold" : unlocked ? "green" : "muted",
      );
      const detail = this.add
        .text(
          x,
          y + (cardHeight < 165 ? 51 : 59),
          unlocked ? tower.description : describeTowerRequirement(tower),
          {
            fontFamily: "Trebuchet MS, sans-serif",
            fontSize: cardHeight < 165 ? "8px" : "9px",
            align: "center",
            color: unlocked ? "#b8cdbf" : "#77716b",
            lineSpacing: 2,
            wordWrap: { width: cardWidth - 20 },
          },
        )
        .setOrigin(0.5);
      this.pageObjects.push(iconPlate, icon, name, badge, detail);
    });

    const unlockedCount = towers.filter((tower) =>
      isTowerUnlocked(tower, this.save.progress),
    ).length;
    const statusPanel = this.add
      .rectangle(this.scale.width / 2, this.scale.height - 110, 420, 42, 0x1a1613, 0.86)
      .setStrokeStyle(2, this.selected.size > 0 ? COLORS.goldDark : COLORS.red, 0.75);
    const status = this.add
      .text(
        this.scale.width / 2,
        this.scale.height - 110,
        `${this.selected.size}/5 companies selected  •  ${unlockedCount}/${towers.length} unlocked`,
        {
          fontFamily: "Georgia, serif",
          fontSize: "16px",
          color: this.selected.size > 0 ? "#fff3cf" : "#e98f84",
        },
      )
      .setOrigin(0.5);
    this.pageObjects.push(statusPanel, status);

    if (pageCount > 1) {
      const previous = addButton(
        this,
        this.scale.width / 2 - 260,
        this.scale.height - 56,
        130,
        "‹ Previous",
        () => {
          this.page -= 1;
          this.renderPage();
        },
      );
      previous.setVisible(this.page > 0);
      const next = addButton(
        this,
        this.scale.width / 2 + 260,
        this.scale.height - 56,
        130,
        "Next ›",
        () => {
          this.page += 1;
          this.renderPage();
        },
      );
      next.setVisible(this.page < pageCount - 1);
      this.pageObjects.push(previous, next);
    }

    const launch = addButton(
      this,
      this.scale.width / 2,
      this.scale.height - 56,
      240,
      "Defend the Keep",
      () => {
        if (this.selected.size === 0) return;
        const towerIds = [...this.selected].slice(0, 5);
        this.registry.set("selected-loadout", towerIds);
        this.save = { ...this.save, loadout: { towerIds, maximumSlots: 5 } };
        writeSave(this.save);
        this.scene.start("ChapterIntro");
      },
      true,
    );
    launch.setAlpha(this.selected.size > 0 ? 1 : 0.45);
    this.pageObjects.push(launch);
  }

  private toggleTower(id: string): void {
    if (this.selected.has(id)) this.selected.delete(id);
    else if (this.selected.size < 5) this.selected.add(id);
    this.renderPage();
  }
}
